// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Left orange accent stripe for Search/Filters section
// - Inputs/autocomplete/select: soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - Tables: blue header, soft zebra rows, hover tint, subtle borders
// - IconButtons: blue default, orange on hover
// - No business/API logic changed

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Checkbox,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  Autocomplete,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import UploadIcon from "@mui/icons-material/Upload";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { API_URL, API_UPLOAD_URL } from "./config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ManageUsers = () => {
  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  // ===== Original state/logic =====
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [addSearchOpts, setAddSearchOpts] = useState([]);
  const [addSearching, setAddSearching] = useState(false);

  const [newRow, setNewRow] = useState({
    campuscode: "",
    username: "",
    FullName: "",
    UserID: "",
    UserKey: "",
    UserType: "",
    password_hash: "",
    roleIds: [],
    is_2fa_enabled: 0,
    google_auth_secret: "",
  });

  const [editRowId, setEditRowId] = useState(null);
  const [editingPasswordId, setEditingPasswordId] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState("");
  const [loginCampusCode, setCampusCode] = useState("");
  const fileInputRef = useRef();
  const token = localStorage.getItem("auth");
  const [uploadErrors, setUploadErrors] = useState([]);

  // Decode campuscode from JWT
  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decoded.campusCode);
      setNewRow((r) => ({ ...r, campuscode: decoded.campusCode }));
    } catch {
      setError("Failed to decode authentication token");
    }
  }, []);

  const downloadSampleExcel = () => {
    const headers = ["campuscode", "Student_Employee_ID", "UserType", "username", "password_hash", "role"];
    const cc = loginCampusCode;

    const sampleRows = [
      {
        campuscode: cc,
        Student_Employee_ID: "226Q1A4206",
        UserType: "Student",
        username: "226Q1A4206",
        password_hash: "Temp@123",
        role: "Student",
      },
      {
        campuscode: cc,
        Student_Employee_ID: "EMP1023",
        UserType: "Employee",
        username: "emp1023",
        password_hash: "Temp@123",
        role: "Student",
      },
      {
        campuscode: cc,
        Student_Employee_ID: "EMP1023",
        UserType: "Employee",
        username: "emp1023",
        password_hash: "Temp@123",
        role: "ClassRepresentative",
      },
    ];

    const normalizedRows = sampleRows.map((r) => {
      const obj = {};
      headers.forEach((h) => (obj[h] = r[h] ?? ""));
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(normalizedRows, { header: headers });
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(16, h.length + 2) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");

    const instructions = [
      ["Field", "How to fill"],
      ["campuscode", "Must match your login campus code"],
      ["Student_Employee_ID", "StudentID or EmployeeID (visible ID). Make sure it exists."],
      ["UserType", "Student or Employee"],
      ["username", "Login username (unique within campus)"],
      ["password_hash", "Plain temp password (backend should hash)"],
      [
        "role",
        "A Role per line per student from these roles Admin,Dean,HOD,ClassTeacher,Teacher,ClassRepresentative,Student",
      ],
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(instructions);
    ws2["!cols"] = [{ wch: 20 }, { wch: 80 }];
    XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `ManageUsers_Sample_${cc}.xlsx`);
  };

  const [roleOptions, setRoleOptions] = useState([]); // [{id, name}]

  useEffect(() => {
    axios
      .get(`${API_URL}/api/roles`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setRoleOptions(res.data))
      .catch(() => setError("Failed to load roles"));
  }, []);

  const searchUsersById = async (text) => {
    if (!text || text.trim().length < 2) {
      setAddSearchOpts([]);
      return;
    }
    setAddSearching(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/Users/search`, {
        params: { q: text },
        headers: { Authorization: `Bearer ${token}` },
      });
      const opts = (data?.users || []).map((u) => ({
        ...u,
        label: `${u.UserID} — ${u.FullName} [${u.UserType}]`,
      }));
      setAddSearchOpts(opts);
    } catch {
      setAddSearchOpts([]);
    } finally {
      setAddSearching(false);
    }
  };

  // Fetch users
  const fetchUsers = (searchText = "") => {
    axios
      .get(`${API_URL}/api/ManageUsers`, {
        params: { Username: searchText },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setData(
          res.data.map((r) => ({
            ...r,
            _original_password_hash: r.password_hash,
            roleIds: r.roleIds || [],
          }))
        );
        setSearchOptions(
          res.data.map((u) => ({
            label: u.FullName ? `${u.FullName} (${u.username})` : u.username,
            id: u.username,
          }))
        );
      })
      .catch(() => setError("Failed to fetch users"));
  };

  useEffect(() => {
    if (loginCampusCode) fetchUsers();
  }, [loginCampusCode]);

  const lookup = (text) => {
    axios
      .get(`${API_URL}/api/ManageUsers/Lookup`, {
        params: { Search: text },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setSearchOptions(
          res.data.map((u) => ({
            label: u.FullName ? `${u.FullName} (${u.username})` : u.username,
            id: u.username,
          }))
        );
      })
      .catch(() => setError("Lookup failed"));
  };

  const handleSearchInputChange = (_, v) => {
    setSearch(v);
    if (v.length >= 1) lookup(v);
    else setSearchOptions([]);
  };

  const handleSearchBarChange = (_, option) => {
    if (option && option.id) {
      setSearch(option.label);
      fetchUsers(option.id);
    } else {
      setSearch("");
      fetchUsers();
    }
  };

  const handleInputChange = (e, id = null) => {
    const { name, value, checked } = e.target;
    const v = name === "is_2fa_enabled" ? (checked ? 1 : 0) : value;

    if (id == null) {
      setNewRow((r) => ({ ...r, [name]: v }));
      return;
    }

    setData((d) =>
      d.map((r) => {
        if (r.ID !== id) return r;

        if (name === "is_2fa_enabled") {
          const current = !!r.is_2fa_enabled;
          const next = !!checked;
          if (!current && next) return r; // do not allow enabling 2FA in edit mode
        }

        return { ...r, [name]: v };
      })
    );
  };

  const handleAddRow = async () => {
    try {
      const payload = {
        campuscode: newRow.campuscode,
        username: newRow.username,
        FullName: newRow.FullName,
        UserID: newRow.UserID,
        UserKey: newRow.UserKey,
        UserType: newRow.UserType,
        password_hash: newRow.password_hash,
        roleIds: newRow.roleIds,
        is_2fa_enabled: newRow.is_2fa_enabled,
        google_auth_secret: newRow.google_auth_secret,
      };
      const { data: created } = await axios.post(`${API_URL}/api/ManageUsers`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData((d) => [...d, created]);
      setNewRow((r) => ({
        ...r,
        username: "",
        FullName: "",
        UserID: "",
        UserKey: "",
        UserType: "",
        password_hash: "",
        roleIds: [],
      }));
      setSuccess(true);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add user");
    }
  };

  const handleUpdateRow = async (id) => {
    const row = data.find((r) => r.ID === id);
    if (!row) return;
    try {
      await axios.put(
        `${API_URL}/api/ManageUsers/${id}`,
        {
          username: row.username,
          FullName: row.FullName,
          role: row.role,
          is_2fa_enabled: row.is_2fa_enabled,
          google_auth_secret: row.google_auth_secret,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await axios.put(
        `${API_URL}/api/ManageUsers/${id}/roles`,
        { roleIds: row.roleIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setEditRowId(null);
      setSuccess(true);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update user/roles");
    }
  };

  const handleSavePassword = async (id) => {
    const row = data.find((r) => r.ID === id);
    if (!row) return;
    try {
      await axios.post(
        `${API_URL}/api/ManageUsers/${id}/password`,
        { password: row.password_hash },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingPasswordId(null);
      setShowPwd(false);
      setSuccess(true);
      setData((d) =>
        d.map((r) => (r.ID === id ? { ...r, _original_password_hash: r.password_hash } : r))
      );
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update password");
    }
  };

  const handleDeleteRow = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/ManageUsers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData((d) => d.filter((r) => r.ID !== id));
      setSuccess(true);
    } catch {
      setError("Failed to delete user");
    }
  };

  const handleBulkClick = () => fileInputRef.current.click();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", loginCampusCode);

    try {
      const res = await axios.post(`${API_URL}/api/UploadManageUsers`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data?.errors?.length) {
        setUploadErrors(res.data.errors);
        setWarning(`Upload completed with ${res.data.errors.length} error(s)`);
        setSuccess(true);
        setError("");
      } else {
        setUploadErrors([]);
        setWarning("");
        setSuccess(true);
      }

      await fetchUsers();
    } catch (err) {
      setUploadErrors([]);
      setError(err.response?.data?.error || "Bulk upload failed");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Update User Security
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Manage roles, reset passwords, and view 2FA status
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" sx={outlinedBtnSx} onClick={downloadSampleExcel}>
              Download Sample
            </Button>

            <Button variant="contained" sx={primaryBtnSx} startIcon={<UploadIcon />} onClick={handleBulkClick}>
              Bulk Upload
            </Button>

            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFile}
            />
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* ✅ Search section */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Search
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Autocomplete
                freeSolo
                options={searchOptions}
                value={search}
                onInputChange={handleSearchInputChange}
                onChange={handleSearchBarChange}
                getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.label)}
                renderInput={(params) => (
                  <TextField {...params} label="Search by ID or Name" size="small" sx={inputSx} />
                )}
                sx={{ maxWidth: 520 }}
              />
            </Box>
          </Paper>

          {/* Messages */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mt: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {warning && (
            <Alert
              severity="warning"
              onClose={() => setWarning("")}
              sx={{
                mt: 2,
                borderRadius: 2,
                border: "1px solid rgba(255,152,0,0.25)",
                backgroundColor: "rgba(255,152,0,0.06)",
              }}
            >
              {warning}
            </Alert>
          )}

          {success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Operation successful!
              </Alert>
            </Snackbar>
          )}

          {uploadErrors.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
              <Typography fontWeight={900} sx={{ mb: 1 }}>
                Some rows failed to upload:
              </Typography>
              <ul style={{ marginLeft: 16, marginTop: 0 }}>
                {uploadErrors.map((e, i) => (
                  <li key={i}>
                    <b>{e.Student_Employee_ID}</b> — {e.message}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {/* ✅ Table */}
          <Paper elevation={0} sx={tableWrapSx}>
              {/* INNER – ONLY for horizontal scroll */}
              <Box
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                }}
              >
            <TableContainer component="div">
            <Table size="small" sx={{ width: "100%" }}>
              <colgroup>
                <col style={{ width: 50 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 220 }} />
                <col style={{ width: 260 }} />
                <col style={{ width: 260 }} />
                <col style={{ width: 60 }} />
                <col style={{ width: 120 }} />
              </colgroup>

              <TableHead>
                <TableRow sx={theadRowSx}>
                  {["#", "Campus", "UserID", "UserType", "Username", "FullName", "Password", "Role", "2FA", "Actions"].map(
                    (h) => (
                      <TableCell key={h}>{h}</TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {data.map((row, idx) => {
                  const isFullEdit = editRowId === row.ID;
                  const isPwdEdit = editingPasswordId === row.ID;

                  return (
                    <TableRow key={row.ID} sx={zebraRowSx(idx)}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{row.campuscode}</TableCell>
                      <TableCell>{row.UserID}</TableCell>
                      <TableCell>{row.UserType}</TableCell>
                      <TableCell>{row.username}</TableCell>
                      <TableCell>{row.FullName}</TableCell>

                      {/* Password Column */}
                      <TableCell>
                        {isPwdEdit ? (
                          <TextField
                            name="password_hash"
                            type={showPwd ? "text" : "password"}
                            value={row.password_hash}
                            onChange={(e) => handleInputChange(e, row.ID)}
                            fullWidth
                            size="small"
                            sx={inputSx}
                            InputProps={{
                              endAdornment: (
                                <IconButton sx={iconBtnSx} onClick={() => setShowPwd((v) => !v)}>
                                  {showPwd ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              ),
                            }}
                          />
                        ) : isFullEdit ? (
                          <TextField
                            name="password_hash"
                            type="password"
                            value={row.password_hash}
                            disabled
                            fullWidth
                            size="small"
                            sx={inputSx}
                          />
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={outlinedBtnSx}
                            onClick={() => {
                              setEditingPasswordId(row.ID);
                              setShowPwd(false);
                              setData((d) => d.map((r) => (r.ID === row.ID ? { ...r, password_hash: "" } : r)));
                            }}
                          >
                            Change Password
                          </Button>
                        )}
                      </TableCell>

                      {/* Roles */}
                      <TableCell>
                        {isFullEdit ? (
                          <Autocomplete
                            multiple
                            size="small"
                            options={roleOptions}
                            getOptionLabel={(o) => o.name}
                            value={roleOptions.filter((opt) => (row.roleIds || []).includes(opt.id))}
                            onChange={(_, newVal) => {
                              const ids = newVal.map((v) => v.id);
                              setData((d) => d.map((r) => (r.ID === row.ID ? { ...r, roleIds: ids } : r)));
                            }}
                            renderInput={(params) => <TextField {...params} label="Roles" sx={inputSx} />}
                          />
                        ) : row.roles?.length ? (
                          row.roles.map((r) => r.roleName).join(", ")
                        ) : (
                          ""
                        )}
                      </TableCell>

                      {/* 2FA */}
                      <TableCell>
                        {isFullEdit ? (
                          <Checkbox
                            name="is_2fa_enabled"
                            checked={!!row.is_2fa_enabled}
                            onChange={(e) => handleInputChange(e, row.ID)}
                            sx={checkboxSx}
                          />
                        ) : (
                          <Checkbox checked={!!row.is_2fa_enabled} readOnly sx={checkboxSx} />
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        {isFullEdit ? (
                          <>
                            <IconButton sx={iconBtnSx} onClick={() => handleUpdateRow(row.ID)} title="Save">
                              <SaveIcon />
                            </IconButton>
                            <IconButton sx={iconBtnSx} onClick={() => setEditRowId(null)} title="Cancel">
                              <CancelIcon />
                            </IconButton>
                          </>
                        ) : isPwdEdit ? (
                          <>
                            <IconButton sx={iconBtnSx} onClick={() => handleSavePassword(row.ID)} title="Save Password">
                              <SaveIcon />
                            </IconButton>
                            <IconButton
                              sx={iconBtnSx}
                              onClick={() => {
                                setEditingPasswordId(null);
                                setShowPwd(false);
                                setData((d) =>
                                  d.map((r) =>
                                    r.ID === row.ID ? { ...r, password_hash: r._original_password_hash } : r
                                  )
                                );
                              }}
                              title="Cancel"
                            >
                              <CancelIcon />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton sx={iconBtnSx} onClick={() => setEditRowId(row.ID)} title="Edit">
                              <EditIcon />
                            </IconButton>
                            <IconButton sx={iconBtnSx} onClick={() => handleDeleteRow(row.ID)} title="Delete">
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* New row */}
                <TableRow sx={zebraRowSx(data.length)}>
                  <TableCell />
                  <TableCell>
                    <TextField value={loginCampusCode} disabled fullWidth size="small" sx={inputSx} />
                  </TableCell>

                  <TableCell>
                    <Autocomplete
                      freeSolo
                      options={addSearchOpts}
                      loading={addSearching}
                      value={newRow.UserID || ""}
                      getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.UserID)}
                      onInputChange={(_, v) => {
                        setNewRow((r) => ({ ...r, UserID: v }));
                        searchUsersById(v);
                      }}
                      onChange={(_, option) => {
                        if (option && typeof option !== "string") {
                          setNewRow((r) => ({
                            ...r,
                            UserID: option.UserID,
                            UserKey: option.UserKey,
                            UserType: option.UserType,
                            FullName: option.FullName || "",
                          }));
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="UserID"
                          placeholder="Type ID"
                          size="small"
                          fullWidth
                          sx={inputSx}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {addSearching ? <CircularProgress size={16} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </TableCell>

                  <TableCell>
                    <Select
                      name="UserType"
                      value={newRow.UserType || ""}
                      onChange={(e) => setNewRow((r) => ({ ...r, UserType: e.target.value }))}
                      displayEmpty
                      fullWidth
                      size="small"
                      sx={inputSx}
                    >
                      <MenuItem value="">
                        <em>Select type</em>
                      </MenuItem>
                      <MenuItem value="Student">Student</MenuItem>
                      <MenuItem value="Employee">Employee</MenuItem>
                    </Select>
                  </TableCell>

                  <TableCell>
                    <TextField
                      name="username"
                      value={newRow.username}
                      onChange={handleInputChange}
                      placeholder="Username"
                      fullWidth
                      size="small"
                      sx={inputSx}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField value={newRow.FullName || ""} placeholder="Full Name" fullWidth disabled size="small" sx={inputSx} />
                  </TableCell>

                  <TableCell>
                    <TextField
                      name="password_hash"
                      type="password"
                      value={newRow.password_hash}
                      onChange={handleInputChange}
                      placeholder="Password"
                      fullWidth
                      size="small"
                      sx={inputSx}
                    />
                  </TableCell>

                  <TableCell>
                    <Autocomplete
                      multiple
                      size="small"
                      options={roleOptions}
                      getOptionLabel={(o) => o.name}
                      value={roleOptions.filter((opt) => (newRow.roleIds || []).includes(opt.id))}
                      onChange={(_, newVal) => {
                        const ids = newVal.map((v) => v.id);
                        setNewRow((r) => ({ ...r, roleIds: ids }));
                      }}
                      renderInput={(params) => <TextField {...params} label="Roles" sx={inputSx} />}
                    />
                  </TableCell>

                  <TableCell>
                    <Checkbox
                      name="is_2fa_enabled"
                      checked={!!newRow.is_2fa_enabled}
                      onChange={handleInputChange}
                      sx={checkboxSx}
                    />
                  </TableCell>

                  <TableCell>
                    <IconButton sx={iconBtnSx} onClick={handleAddRow} title="Add">
                      <AddIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default ManageUsers;
